import { engineComponent } from "neo-common";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { ModuleManager } from "../../Managers/ModuleManager";
import { Tweens } from "../../Components/Effects/Tweens";
import { TeasingComponent } from "../../CustomComponents/TeasingComponent";
import { TeasingBonus } from "../../CustomComponents/TeasingBonus";
import { PlayList } from "../../Components/Sounds/PlayList";
import { DataManager } from "../../Managers/DataManager";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { PlaneRopeElement } from "../../CustomComponents/PlaneRopeElement";
import { AmbientBackground } from "../../Utils/AmbientBackground";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends ContainerElement {
    private _controller: BaseController;
    private _wasFinished: boolean = false;
    private _outroDelay: FrameTimerEventEmitter;
    private _goToScene: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _paylinesSelector: PaylinesSelector;

    private _symbols: Symbols;
    private _machine: Machine;
    private _noWinContainer: ContainerElement;
    private _winAnimator: WinAnimatorExtended;

    private _background: SwitchElement;
    private _reelBorder: SwitchElement;
    private _crissCross: ContainerElement;
    private _foreground: SwitchElement;
    private _foregroundUnder: SwitchElement;
    private _curtain: SwitchElement;

    private _planeRopeElements: Array<PlaneRopeElement> = [];

    /* Switch elements animated during win (and others?) */
    private _actionElements: Array<SwitchElement> = [];

    private _teasingBonus: TeasingBonus;

    private _curtainStartDelay: number = 120; // 2 sec

    private _transition: SwitchElement;

    constructor() {
        super();

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._goToScene = CreateFrameTimer("goToScene");
        this._goToScene.on(FrameTimerEvents.Complete, this.goToScene.bind(this));

        this._teasingBonus = new TeasingBonus();
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        let actionElementNames: Array<string> = [];
        if (node.hasAttribute(BaseViewAttributes.ACTION_ELEMENTS)) {
            actionElementNames = node.getAttribute(BaseViewAttributes.ACTION_ELEMENTS).split(";");
        }

        if (this.childs != null) {
            for (let key in this.childs) {
                for (let i = 0; i < actionElementNames.length; i++) {
                    if (key == actionElementNames[i] && this.childs[key] instanceof SwitchElement) {
                        this._actionElements.push(this.childs[key]);
                    }
                }

                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof PaylinesSelector) {
                    this._paylinesSelector = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof Machine) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "TeasingComponent" && this.childs[key] instanceof TeasingComponent) {
                    this._teasingBonus = this.childs[key];
                }

                if (key === "TeasingFront" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingFront: this.childs[key] });
                    }
                }

                if (key === "TeasingParticles" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingParticles: this.childs[key] });
                    }
                }

                if (key === "Background" && this.childs[key] instanceof SwitchElement) {
                    this._background = this.childs[key];

                    if (this._background) {
                        this._planeRopeElements.push(
                            <PlaneRopeElement>this._background.getChildElementByName("CurtainLeftUpscale"),
                        );
                        this._planeRopeElements.push(
                            <PlaneRopeElement>this._background.getChildElementByName("CurtainRightUpscale"),
                        );
                    }
                }

                if (key === "ReelBorder" && this.childs[key] instanceof SwitchElement) {
                    this._reelBorder = this.childs[key];
                }

                if (key === "CrissCross" && this.childs[key] instanceof ContainerElement) {
                    this._crissCross = this.childs[key];
                }

                if (key === "ForegroundOver" && this.childs[key] instanceof SwitchElement) {
                    this._foreground = this.childs[key];
                }
                if (key === "ForegroundUnder" && this.childs[key] instanceof SwitchElement) {
                    this._foregroundUnder = this.childs[key];
                }

                if (key === "CurtainMiddle" && this.childs[key] instanceof SwitchElement) {
                    this._curtain = this.childs[key];
                }

                if (key === "Transition" && this.childs[key] instanceof SwitchElement) {
                    this._transition = this.childs[key];
                }
            }

            if (this._symbols == null) {
                if (DEBUG) {
                    this.log("ERROR no symbols");
                }
            }

            if (this._machine == null) {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }

            if (this._paylines == null) {
                if (DEBUG) {
                    this.log("WARNING no paylines");
                }
            }

            if (this._winAnimator == null) {
                if (DEBUG) {
                    this.log("WARNING no winAnimator");
                }
            }
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels !== null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(this._symbols, GameManager.baseResponse.initData.reels);
                        this._machine.addElementInstance({ teasing: [this._teasingBonus] });
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            this._winAnimator.initialize(this._symbols, this._paylines, this._noWinContainer);
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                            this._winAnimator.addElementInstance({ teasing: [this._teasingBonus] });
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }
    }

    disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    initIntro(): void {
        if (GameManager.gameResponse.totalWin > 0 || ModuleManager.freespinsEnabled()) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecaploop");
            this.stopAnimationCurtain();
        } else {
            PlayList.stop("SND_BaseRecaploop");
            PlayList.loop("SND_BaseLoop");
            this.startAnimationCurtain(this._curtainStartDelay);
        }
        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
    }

    collect(): void {
        PlayList.stop("SND_FSEndLoop");
        PlayList.stop("SND_BaseRecaploop");
        PlayList.loop("SND_BaseLoop");
        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }
        this._winAnimator.reset(true);

        this.startAnimationCurtain(this._curtainStartDelay);
    }

    spin(): void {
        PlayList.stop("SND_FSEndLoop");
        PlayList.stop("SND_BaseRecaploop");
        PlayList.loop("SND_BaseLoop");
        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }
        this._machine.spinStart(this._winAnimator.reset(false, true));

        this.stopAnimationCurtain();
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(
            GameManager.baseResponse.screen.reels,
            this._machine.actualReelStopped,
            this._machine.forceStop,
        );
    }

    machineStopped(): void {
        this._wasFinished = false;
        this._controller.machineStopped();

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.stop("SND_BaseLoop");
        }

        if (
            GameManager.baseResponse.wins &&
            GameManager.baseResponse.wins.length &&
            GameManager.baseResponse.currentWin
        ) {
            if (this._winAnimator.getCurrentMultiplierIndex() > 1) {
                this.setActionElementsToState("HIGH_WIN_" + (this._winAnimator.getCurrentMultiplierIndex() - 1));
            } else {
                this.setActionElementsToState("WIN");
            }
        } else {
            this.setActionElementsToState("NORMAL");
            if (!ModuleManager.freespinsEnabled()) {
                this.startAnimationCurtain(this._curtainStartDelay);
            }
        }
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        this.setActionElementsToState("NORMAL_QUICK");
        if (!DataManager.Autoplay && ModuleManager.freespinsEnabled() === false) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecaploop");
        }
        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false): void {
        this.visible = true;

        if (long) {
            if (this._winAnimator != null) {
                this._winAnimator.alpha = 0;
            }

            if (this._transition != null) {
                this._transition.goToCase("BASE_INTRO");
            }
        }

        this._winAnimator.alpha = 1;
        //(<ImageElement>this._background.getChildElementByName("ReelsBorder")).position.y = 1;
        this._reelBorder.position.y = 1;
        this._crissCross.position.y = 0;
        this._winAnimator.position.y = 0;
        this._foreground.position.y = 0;
        this._foregroundUnder.position.y = 0;

        this.introFinished();
    }

    introFinished(): void {
        if (GameManager.gameResponse.totalWin > 0) {
            PlayList.stop("SND_BaseLoop");
            if (!PlayList.isPlaying("SND_BaseRecaploop")) {
                PlayList.loop("SND_BaseRecaploop");
            }
        } else {
            PlayList.stop("SND_BaseRecaploop");
            PlayList.loop("SND_BaseLoop");
            this.startAnimationCurtain(this._curtainStartDelay);
        }

        this._controller.introFinished();
    }

    /**
     * Delayed move to scene.
     */
    goToScene(): void {
        // PlayList.stop('SND_FSTransitionSelect', 60);
        // PlayList.play('SND_FSTransitionPrelet');
    }

    outro(): void {
        if (ModuleManager.freespinsActive()) {
            if (this._transition != null) {
                this._transition.goToCase("BASE_OUTRO");
            }

            this._outroDelay.start(60);
            this._winAnimator.position.y = -995;
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecaploop");

            if (this._paylinesSelector != null) {
                this._paylinesSelector.reset();
            }

            let wasFinished: boolean = this._wasFinished;
            let inHighWins: boolean = this._winAnimator.reset(true);

            if (wasFinished) {
                this.outroFinished();
            } else {
                if (inHighWins) {
                    this._outroDelay.start(60);
                } else {
                    this._outroDelay.start(30);
                }
            }
        }
        this.stopAnimationCurtain();
    }

    outroFinished(): void {
        this._outroDelay.stop();
        // PlayList.stop('SND_FSTransitionPrelet', 120);
        if (ModuleManager.freespinsActive()) {
            this.visible = false;
        }

        if (ModuleManager.freespinsActive()) {
            AmbientBackground.changeAmbientBackground("color-1", 100);
        }

        this._controller.outroFinished();
    }

    selectPayline(): void {}

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecaploop");
        }
    }

    private setActionElementsToState(state: string): void {
        this._actionElements.forEach((element) => {
            if (element.hasCase(state) && element.getCurrentCase() != state) {
                element.goToCase(state);
            }
        });
    }

    private stopAnimationCurtain(): void {
        if (this._planeRopeElements && this._planeRopeElements.length > 0) {
            this._planeRopeElements.forEach((element) => {
                element.stop();
            });
        }
    }

    private startAnimationCurtain(startDelay: number): void {
        if (this._planeRopeElements && this._planeRopeElements.length > 0) {
            this._planeRopeElements.forEach((element) => {
                element.start(startDelay);
            });
        }
    }
}

export const BaseViewAttributes = {
    ACTION_ELEMENTS: "ActionElements",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/